import {
  Customer,
  CustomerBusinessOperationsCustomers,
  CustomerBusinessOperationsFundsSource,
  CustomerBusinessOperationsProfile,
  CustomerCompanyDetails,
  CustomerDocuments,
  CustomerOrganizationContact,
  CustomerOrganizationLegalRepresentative,
  CustomerOrganizationOwners,
  CustomerSliceType,
  CustomerSocialNetwork,
  isCustomerSliceType,
} from '@/types/customer';
import { createValidationSchema, hiddenFieldsFilter } from '@/types/fields';
import { ChildStep, ParentStep, Status } from '@/types/steps';
import { getStepSkipMessage } from '@/utility/getStepSkipMessage';

export const extractCustomerSlice = (
  type: CustomerSliceType,
  customer: Customer,
):
  | CustomerCompanyDetails
  | CustomerBusinessOperationsProfile
  | CustomerBusinessOperationsCustomers
  | CustomerBusinessOperationsFundsSource
  | CustomerOrganizationLegalRepresentative
  | CustomerOrganizationContact
  | CustomerSocialNetwork
  | CustomerOrganizationOwners
  | undefined => {
  switch (type) {
    case CustomerSliceType.companyDetails:
      return CustomerCompanyDetails.fromCustomer(customer);
    case CustomerSliceType.businessOperationsProfile:
      return CustomerBusinessOperationsProfile.fromCustomer(customer);
    case CustomerSliceType.businessOperationsCustomers:
      return CustomerBusinessOperationsCustomers.fromCustomer(customer);
    case CustomerSliceType.businessOperationsFundsSource:
      return CustomerBusinessOperationsFundsSource.fromCustomer(customer);
    case CustomerSliceType.organizationLegalRepresentative:
      return CustomerOrganizationLegalRepresentative.fromCustomer(customer);
    case CustomerSliceType.organizationContact:
      return CustomerOrganizationContact.fromCustomer(customer);
    case CustomerSliceType.socialNetwork:
      return CustomerSocialNetwork.fromCustomer(customer);
    case CustomerSliceType.organizationOwners:
      return CustomerOrganizationOwners.fromCustomer(customer);
    default:
      throw new Error(`Unknown slice type: ${type}`);
  }
};

export const getCompletedStepsCount = (
  steps: readonly ParentStep<unknown>[],
  statuses: Record<string, Status>,
  customer: Customer | undefined,
): number => {
  const filterSkipped = (step: ChildStep<unknown>): boolean => {
    return getStepSkipMessage(step, customer) === undefined;
  };

  return steps.reduce((total: number, { children }: ParentStep<unknown>): number => {
    const reducer = (total: number, child: ChildStep<unknown>): number => {
      return total + (statuses[child.type] === Status.completed ? 1 : 0);
    };

    return (
      total +
      children //
        .filter(filterSkipped) //
        .reduce(reducer, 0)
    );
  }, 0);
};

const shouldSkip = (child: ChildStep<unknown>, data: unknown): boolean => {
  const { skip } = child;
  if (skip === undefined) {
    return false;
  } else if (typeof skip === 'function') {
    if (data === null) {
      return false;
    }

    return skip(data) !== undefined;
  }

  return true;
};

export const getTotalStepsCount = (
  steps: readonly ParentStep<unknown>[],
  data: unknown,
): number => {
  return steps.reduce((total: number, { children }: ParentStep<any>): number => {
    return (
      total +
      children.reduce((childCount: number, childStep: ChildStep<unknown>): number => {
        if (shouldSkip(childStep, data)) {
          return childCount;
        }

        return childCount + 1;
      }, 0)
    );
  }, 0);
};

const computeStepStatus = (child: ChildStep<any>, customer: Customer): Status => {
  const { type, fields } = child;

  const skipMessage = getStepSkipMessage(child, customer);
  if (skipMessage) {
    return Status.completed;
  }

  // TODO: cache this somehow while keeping in mind that it depends on the current value.
  const validationSchema = createValidationSchema(fields.filter(hiddenFieldsFilter(customer)));
  try {
    if (isCustomerSliceType(type)) {
      const currentSlice = extractCustomerSlice(type, customer);
      validationSchema.validateSync(currentSlice);
    } else if (type === 'documents') {
      const { documents = CustomerDocuments.empty() } = customer.businessInfo ?? {};
      validationSchema.validateSync(documents);
    }

    return Status.completed;
  } catch {
    // Don't need to do anything -\_('-')_/- //
    return Status.error;
  }
};

export const computeStepsStatuses = (
  steps: readonly ParentStep<unknown>[],
  customer: Customer | undefined,
): Record<string, Status> => {
  if (!customer) {
    return {};
  }

  return steps.reduce(
    (statuses: Record<string, Status>, parent: ParentStep<unknown>): Record<string, Status> => {
      const { children } = parent;

      return {
        ...statuses,
        ...children.reduce(
          (
            childrenStatuses: Record<string, Status>,
            child: ChildStep<unknown>,
          ): Record<string, Status> => {
            return {
              ...childrenStatuses,
              [child.type]: computeStepStatus(child, customer),
            };
          },
          {},
        ),
      };
    },
    {},
  );
};
